import {
  ExpandedMenu,
  SecondaryNavContext,
} from "components/AppLayout/SecondaryNavContext";
import { Button } from "components/DesignSystem/Button/Button";
import { ProfileDropdown } from "components/ProfileDropdown/ProfileDropdown";
import { LeftBarOpenContext } from "components/Sidebar/LeftBar";
import { Cross } from "components/icons/Cross";
import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useCallback, useMemo, useState } from "react";

type MobileNavProps = {
  open: boolean;
  onClose: () => void;
  logo?: ReactNode;
  subHeader?: ReactNode;
  stickyNav?: ReactNode;
  useInternalLink?: boolean;
  children: ReactNode;
};

const LEFT_BAR_OPEN = { open: true };

export const MobileNav = ({
  open,
  onClose,
  logo,
  subHeader,
  stickyNav,
  useInternalLink,
  children,
}: MobileNavProps) => {
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
  const [bodySlot, setBodySlot] = useState<HTMLElement | null>(null);
  const [activeCount, setActiveCount] = useState(0);
  const [expandedMenu, setExpandedMenu] = useState<ExpandedMenu | null>(null);

  const registerActive = useCallback(() => {
    setActiveCount((count) => count + 1);
    return () => setActiveCount((count) => Math.max(0, count - 1));
  }, []);

  const secondaryNavValue = useMemo(
    () => ({
      headerSlot,
      bodySlot,
      active: activeCount > 0,
      collapsed: false,
      setCollapsed: () => {},
      registerActive,
      isInline: true,
      expandedMenu,
      setExpandedMenu,
    }),
    [headerSlot, bodySlot, activeCount, registerActive, expandedMenu]
  );

  return (
    <AnimatePresence>
      {open && (
        <LeftBarOpenContext.Provider value={LEFT_BAR_OPEN}>
          <SecondaryNavContext.Provider value={secondaryNavValue}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={onClose}
              className="t-fixed t-inset-0 t-z-sidebar t-bg-neutral-100/40 md:t-hidden"
            />

            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.2 }}
              className="t-fixed t-inset-y-0 t-left-0 t-z-sidebar t-flex t-w-72 t-max-w-[85vw] t-flex-col t-bg-surface md:t-hidden"
            >
              <div className="t-flex t-shrink-0 t-items-center t-gap-2 t-border-0 t-border-b t-border-solid t-border-neutral-10 t-p-3">
                <div className="t-min-w-0 t-flex-1">{logo}</div>
                <Button customType="ghost_icon" size="xsmall" onClick={onClose}>
                  <Cross />
                </Button>
              </div>

              {subHeader && (
                <div className="t-flex t-shrink-0 t-flex-col t-gap-2 t-border-0 t-border-b t-border-solid t-border-neutral-10 t-p-3">
                  {subHeader}
                </div>
              )}

              <div
                onClick={onClose}
                className="t-min-h-0 t-flex-1 t-overflow-y-auto t-px-3 t-py-2"
              >
                {/* Plain wrapper: navbars use t-h-full for the desktop sidebar,
                    which would otherwise stretch to the drawer height here. */}
                <div>{children}</div>

                {/* The active trigger already names the section, so its
                    sub-menu title isn't repeated here. */}
                <div ref={setHeaderSlot} className="t-hidden" />
                <div
                  ref={setBodySlot}
                  className="t-flex t-flex-col t-gap-0.5 t-pl-4"
                />
              </div>

              <div className="t-shrink-0">
                {stickyNav && (
                  <div
                    onClick={onClose}
                    className="t-flex t-flex-col t-gap-0.5 t-border-0 t-border-b t-border-t t-border-solid t-border-neutral-10 t-px-3 t-py-1"
                  >
                    {stickyNav}
                  </div>
                )}

                <ProfileDropdown useInternalLink={useInternalLink} />
              </div>
            </motion.div>
          </SecondaryNavContext.Provider>
        </LeftBarOpenContext.Provider>
      )}
    </AnimatePresence>
  );
};
